import { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';

import { useVideoFilter } from 'video/hooks/useVideoFilter';
import { IVideoListItemFragment, VideosDocument } from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { usePagination } from 'common/hooks/usePagination';

export const useVideoList = (filterParamsInUrl = true) => {
    const { showApolloError } = useApolloError();

    const {
        filters,
        filterValues,
        filterValuesForQuery,
        handleSearch: onSearch,
        handleFilterChange: onFilterChange,
        loading: loadingFilters,
    } = useVideoFilter(filterParamsInUrl);

    const { paginationSettings, initializePagination, setPage } =
        usePagination(true);

    const paginationVariables = useMemo(
        () => ({
            offset: paginationSettings.offset,
            first: paginationSettings.first,
        }),
        [paginationSettings]
    );

    // Build variables for query
    const variables = useMemo(
        () => ({
            ...paginationVariables,
            ...filterValuesForQuery,
        }),
        [paginationVariables, filterValuesForQuery]
    );

    const {
        loading: videosLoading,
        data: videosData,
        error,
    } = useQuery(VideosDocument, {
        variables,
    });

    useEffect(() => {
        if (error) {
            showApolloError(error);
        }
    }, [error, showApolloError]);

    const videosObject = videosData?.videos;
    const videosCount = videosObject?.count;
    const videos = useMemo(
        () =>
            (videosObject?.edges
                .map((edge) => edge?.node || null)
                .filter(Boolean) as IVideoListItemFragment[]) || [],
        [videosObject?.edges]
    );

    const loading = videosLoading || loadingFilters;

    useEffect(() => {
        if (loading || !initializePagination) return;

        initializePagination(videosCount || 0);
    }, [videosCount, loading, initializePagination]);

    return {
        videos,
        videosCount,
        loading,
        paginationSettings,
        filters,
        filterValues,
        onSearch,
        onFilterChange,
        setPage,
    };
};
